import { useState, useEffect, forwardRef, createRef, useContext, useRef } from "react";
import {
    Box, Button, TextField, useMediaQuery, Typography, useTheme, Grid,
    FormControlLabel, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Slide, Backdrop, CircularProgress, Alert, AlertTitle
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";

import { ColorModeContext, tokens } from "../../../theme";
import { setLogin, setCsrfToken, setMode } from "../../../state";
import { useFetchingData } from '../../../components/api.js';
import logo from '../../../images/logo.png';
import Rocket from "../../../components/Rocket";
import Preloading from "../../../components/Preloading";

// ---------- Yup Schemas (ไม่เปลี่ยน) ----------
const registerSchema = yup.object().shape({
    firstname: yup.string().required(""),
    lastname: yup.string().required(""),
    email: yup.string().email("invalid email").required(""),
    packages: yup.array().min(1, "Select at least one package").notRequired(),
    plan: yup.string().required("required"),
    slip_image: yup.string().required("required"),
});

const loginSchema = yup.object().shape({
    email: yup.string().email("Invalid email").required(""),
});

const initialValuesRegister = {
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    picture: "",
    packages: [],
    total_price: 0,
    slip_image: null,
    plan: "monthly"
};

const initialValuesLogin = {
    email: "",
    password: "",
};

// ---------- JWT parser (ไม่เปลี่ยน) ----------
const parseJwt = (token) => {
    var base64Url = token.split(".")[1];
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    var jsonPayload = decodeURIComponent(
        window.atob(base64).split("").map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
    );
    return JSON.parse(jsonPayload);
};

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// ---------- Main Component ----------
const Form = () => {
    const { __fetching_data__ } = useFetchingData();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { palette } = useTheme();
    const colorMode = useContext(ColorModeContext);

    const [pageType, setPageType] = useState("login");
    const isLogin = pageType === "login";
    const isRegister = pageType === "register";
    const isNonMobile = useMediaQuery("(min-width:600px)");

    const [open, setOpen] = useState(false);           // forget password dialog
    const [openSignUp, setOpenSignUp] = useState(false);
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [openDialogOtp, setOpenDialogOtp] = useState(false);
    const [user, setUser] = useState(null);

    const csrfToken = useSelector((state) => state.csrfToken);
    const [fileName, setFileName] = useState('Upload Slip');
    const [loading, setLoading] = useState(false);
    const [isLoginStatus, setLoginStatus] = useState(false);
    const [isLoginStatusText, setLoginStatusText] = useState("");
    const [isRegisterStatus, setRegisterStatus] = useState(false);
    const [packages, setPackage] = useState(null);
    const [otpMessage, setOtpMessage] = useState("");
    const [otpVerify, setOtpVerify] = useState(null);
    const [otp, setOtp] = useState(new Array(6).fill(''));
    const inputRefs = useRef([]);

    // Popup error type (UI ไฟล์ 2)
    const [popupType, setPopupType] = useState("");
    const [openErrorPopup, setOpenErrorPopup] = useState(false);

    const recaptchaRef = createRef();
    const [recaptchaValue, setRecaptchaValue] = useState(null);
    const [remember, setRemember] = useState(false);

    // ดึง remembered email
    useEffect(() => {
        const savedEmail = localStorage.getItem("rememberedEmail");
        if (savedEmail) setRemember(true);
    }, []);

    const getPackage = async () => {
        const roleResponse = await __fetching_data__({ url: `${window.config.base_api_url}/get_package`, method: "GET", headers: { "xsrf-token": csrfToken } });
        setPackage(roleResponse.data);
    };
    useEffect(() => { getPackage(); }, []);

    // ---------- Handlers (logic เดิมจากไฟล์ 1 ทั้งหมด) ----------
    const forgetPasswordClick = () => setOpen(true);
    const forgetPasswordClose = () => setOpen(false);

    const signUpClick = () => {
        setOpenSignUp(true);
        setPageType("register");
    };
    const signUpClose = () => {
        setOpenSignUp(false);
        setPageType("login");
    };

    const handleOpenBackdrop = () => setOpenBackdrop(true);
    const handleCloseBackdrop = () => setOpenBackdrop(false);
    const handleToggleBackdrop = () => setOpenBackdrop(prev => !prev);

    const handleFormSubmit = async (values, onSubmitProps) => {
        if (isLogin) await login(values, onSubmitProps);
        if (isRegister) await register(values, onSubmitProps);
    };

    const register = async (values, onSubmitProps) => {
        const formData = new FormData();
        for (let value in values) {
            if (value !== "slip_image") formData.append(value, values[value]);
        }
        if (values.slip_image) formData.append('slip_image', values.slip_image);

        const registerResponse = await __fetching_data__({
            url: `${window.config.base_api_url}/register`,
            method: "POST",
            headers: { "xsrf-token": csrfToken },
            body: formData,
            isUploadImg: true
        });

        if (registerResponse.status) {
            setRegisterStatus(true);
            onSubmitProps.resetForm();
            setTimeout(() => { signUpClose(); setRegisterStatus(false); }, 10000);
        }
    };

    const login = async (values, onSubmitProps) => {
        handleLogin(values, "local", onSubmitProps);
    };

    const loginGoogle = async (values) => {
        handleLogin(values, "google");
    };

    const handleLogin = async (values, type, onSubmitProps) => {
        setLoading(true);
        setLoginStatus(false);

        let value = null;
        if (type === "local") {
            value = values;
        } else if (type === "google") {
            const decodedGoogle = parseJwt(values.credential);
            value = {
                email: decodedGoogle.email,
                firstname: decodedGoogle.given_name,
                lastname: decodedGoogle.family_name,
                auth_is_google: true,
                aud: decodedGoogle.aud, azp: decodedGoogle.azp,
                email_verified: decodedGoogle.email_verified,
                exp: decodedGoogle.exp, iat: decodedGoogle.iat,
                iss: decodedGoogle.iss, jti: decodedGoogle.jit,
                locale: decodedGoogle.locale, fullname: decodedGoogle.name,
                nbf: decodedGoogle.nbf, picture: decodedGoogle.picture,
                sub: decodedGoogle.sub,
            };
        }

        handleToggleBackdrop();
        const loggedInResponse = await __fetching_data__({
            url: `${window.config.base_api_url}/login`,
            method: "POST",
            headers: { "xsrf-token": csrfToken },
            body: JSON.stringify(value)
        });

        if (loggedInResponse.status) {
            setUser({ userId: loggedInResponse.userId, token: loggedInResponse.token });
            handleOpenDialogOtp();
            if (remember && value?.email) localStorage.setItem("rememberedEmail", value.email);
            else localStorage.removeItem("rememberedEmail");
        } else {
            setLoginStatus(true);
            setLoginStatusText(loggedInResponse.message);
            // ใช้ popup UI ไฟล์ 2
            setPopupType("loginFailed");
            setOpenErrorPopup(true);
        }

        setLoading(false);
        handleCloseBackdrop();
        setOtp(new Array(6).fill(''));
    };

    const handleOpenDialogOtp = () => {
        setOpenDialogOtp(true);
        setTimeout(() => { inputRefs.current[0]?.focus(); }, 100);
    };

    const handleCloseDialogOtp = () => {
        setOpenDialogOtp(false);
        setOtpVerify(null);
    };

    const handleSubmitOTP = async () => {
        const value = { userId: user.userId, otp: otp.join(''), type: "VERIFICATION" };
        const otpResponse = await __fetching_data__({
            url: `${window.config.base_api_url}/email/otp`,
            method: "POST",
            headers: { "xsrf-token": csrfToken },
            body: JSON.stringify(value)
        });

        if (otpResponse.status) {
            dispatch(setLogin({ user: user.userId, token: user.token }));
            setOtpVerify(true);
            setOpenDialogOtp(false);
            navigate("/");
        } else {
            setOtpMessage("");
            setOtpVerify(false);
        }
    };

    useEffect(() => {
        if (otpVerify === false) {
            setTimeout(() => { inputRefs.current[inputRefs.current.length - 1]?.focus(); }, 100);
        }
    }, [otpVerify]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (window.google) {
                clearInterval(interval);
                window.google.accounts.id.initialize({
                    client_id: "304685095232-2ptjt24p392eqi9qlr4ot6fs1g51ekje.apps.googleusercontent.com",
                    callback: loginGoogle,
                });
                window.google.accounts.id.renderButton(document.getElementById("signInDiv"), { theme: "outline", size: "large" });
                window.google.accounts.id.prompt();
            }
        }, 300);
        return () => clearInterval(interval);
    }, []);

    const handleChangeOTP = (element, index) => {
        if (isNaN(element.value)) return;
        setOtpVerify(null);
        if (element.value && !/^\d$/.test(element.value)) return;
        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp);
        if (element.value && index < 5) inputRefs.current[index + 1]?.focus();
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) inputRefs.current[index - 1].focus();
    };

    const handleKeyUp = (e, index) => {
        if (index === 5 && otp[index] && otp.length === 6) {
            setTimeout(() => { handleSubmitOTP(); }, 100);
        }
    };

    const handleNextPage = () => navigate("/regis2");

    const updateTotalPrice = (selectedPackage, plan) => {
        if (!packages) return 0;
        return packages
            .filter(pack => selectedPackage.includes(pack.role_id))
            .reduce((total, pack) => total + pack.package_price[plan], 0);
    };

    // ================================================================
    // UI (จากไฟล์ 2) + Logic จากไฟล์ 1
    // ================================================================
    return (
        <Formik
            key={isLogin ? 'login' : 'register'}
            onSubmit={handleFormSubmit}
            initialValues={isLogin ? initialValuesLogin : initialValuesRegister}
            validationSchema={isLogin ? loginSchema : registerSchema}
        >
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, resetForm }) => (
                <form onSubmit={handleSubmit}>

                    {/* ===== LOGIN PAGE (UI ไฟล์ 2) ===== */}
                    {isLogin && (
                        <div className="min-h-screen flex items-center justify-center bg-neutral-700 px-4">
                            <div className="
                                w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-6xl mx-auto
                                rounded-2xl lg:rounded-[3rem] overflow-hidden
                                bg-gradient-to-br from-slate-900 to-slate-800
                                shadow-2xl
                            ">
                                {/* Top content */}
                                <div className="
                                    grid grid-cols-1 lg:grid-cols-2
                                    px-6 sm:px-8 md:px-12 lg:px-16
                                    py-8 sm:py-10 md:py-12 lg:py-14
                                    gap-8 md:gap-10 lg:gap-16
                                ">
                                    {/* LEFT */}
                                    <div className="flex flex-col items-center justify-center text-center h-full">
                                        <h2 className="hidden lg:block text-4xl font-semibold text-blue-400 mb-6">
                                            Welcome to
                                        </h2>
                                        <img
                                            src={logo}
                                            alt="Idea Trade"
                                            className="w-40 sm:w-52 md:w-64 lg:w-72 mb-4 md:mb-6"
                                        />
                                        <p className="text-blue-400 font-semibold hidden sm:block text-lg md:text-2xl">
                                            Special Features for our customers
                                        </p>
                                    </div>

                                    {/* RIGHT */}
                                    <div className="flex flex-col justify-center gap-5 text-white">
                                        {/* Email Input */}
                                        <div className="relative w-full">
                                            <input
                                                type="email"
                                                name="email"
                                                value={values.email}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                className="
                                                    peer w-full bg-transparent
                                                    border border-blue-300/40
                                                    rounded-md px-4 py-3 text-white
                                                    focus:border-blue-500 outline-none
                                                "
                                            />
                                            <label className={`
                                                absolute left-4 px-2 py-0.5 rounded-full
                                                text-sm transition-all duration-200
                                                ${values.email
                                                    ? "-top-3 text-xs text-sky-400 bg-slate-800"
                                                    : "top-3 text-sm text-blue-300 bg-transparent"
                                                }
                                                peer-focus:-top-3 peer-focus:text-xs
                                                peer-focus:text-sky-400 peer-focus:bg-slate-800
                                            `}>
                                                EMAIL
                                            </label>
                                            {touched.email && errors.email && (
                                                <p className="text-red-400 text-xs mt-1 pl-1">{errors.email}</p>
                                            )}
                                        </div>

                                        {/* Options */}
                                        <div className="flex justify-between items-center text-sm">
                                            <label className="flex items-center gap-2 cursor-pointer text-gray-300">
                                                <input
                                                    type="checkbox"
                                                    checked={remember}
                                                    onChange={(e) => setRemember(e.target.checked)}
                                                />
                                                Remember me
                                            </label>
                                            <span
                                                onClick={forgetPasswordClick}
                                                className="text-sky-400 cursor-pointer hover:underline text-xs"
                                            >
                                                Forget your ID or password?
                                            </span>
                                        </div>

                                        {/* Sign In Button */}
                                        <button
                                            type="button"
                                            onClick={() => handleFormSubmit(values, { resetForm })}
                                            disabled={loading}
                                            className={`mt-2 py-3 rounded-lg text-lg font-semibold transition-all ${
                                                loading ? "bg-slate-600 cursor-not-allowed" : "bg-sky-600 hover:bg-sky-500"
                                            }`}
                                        >
                                            {loading ? "Signing in..." : "SIGN IN"}
                                        </button>

                                        <p className="text-sm text-center text-gray-400">
                                            Don&apos;t have an account?{" "}
                                            <span
                                                onClick={signUpClick}
                                                className="text-white cursor-pointer hover:underline"
                                            >
                                                Sign up here
                                            </span>
                                        </p>

                                        {/* Google Sign In */}
                                        {/* <div id="signInDiv" className="flex justify-center"></div> */}
                                    </div>
                                </div>

                                {/* Bottom CTA — always row */}
                                <div style={{display: 'none'}} className="bg-slate-700/50 px-4 sm:px-8 md:px-12 lg:px-16 py-5 md:py-8 flex flex-row gap-3 sm:gap-4 md:gap-6">
                                    <button
                                    disabled
                                        type="button"
                                        onClick={() => navigate("/free")}
                                        className="
                                            flex-1 py-3 sm:py-4 md:py-5
                                            rounded-xl md:rounded-2xl
                                            bg-emerald-400 hover:bg-emerald-500
                                            active:scale-95 transition-all duration-200
                                            text-sm sm:text-base md:text-xl font-semibold
                                            flex items-center justify-center gap-2
                                            shadow-lg hover:shadow-emerald-400/40
                                        "
                                    >
                                        🚀 TRY FREE VERSION
                                    </button>
                                    <button
                                    disabled
                                        type="button"
                                        onClick={() => navigate("/membership")}
                                        className="
                                            flex-1 py-3 sm:py-4 md:py-5
                                            rounded-xl md:rounded-2xl
                                            bg-sky-600 hover:bg-sky-700
                                            active:scale-95 transition-all duration-200
                                            text-sm sm:text-base md:text-xl font-semibold
                                            flex items-center justify-center gap-2
                                            shadow-lg hover:shadow-sky-500/40 disable
                                        "
                                    >
                                        👑 JOIN MEMBERSHIP
                                    </button>
                                </div>
                            </div>

                            {/* <Preloading open={loading} /> */}
                        </div>
                    )}

                    {/* ===== REGISTER DIALOG (UI ไฟล์ 2 ผสมกับ logic ไฟล์ 1) ===== */}
                    <Dialog
                        open={openSignUp}
                        onClose={signUpClose}
                        maxWidth="md"
                        fullWidth
                        fullScreen
                    >
                        {isRegisterStatus && (
                            <Alert severity="success" sx={{ position: 'relative', zIndex: 2 }}>
                                สมัครสมาชิกสำเร็จ
                            </Alert>
                        )}

                        {/* Register UI: ใช้ Tailwind เหมือนไฟล์ 2 */}
                        <div className="min-h-screen flex items-center justify-center bg-neutral-700 px-4">
                            <div className="
                                w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-5xl mx-auto
                                rounded-2xl lg:rounded-[3rem] overflow-hidden
                                bg-gradient-to-br from-slate-900 to-slate-800
                                shadow-2xl
                            ">
                                <div className="grid grid-cols-1 lg:grid-cols-2 px-6 sm:px-8 md:px-12 lg:px-16 py-8 md:py-12 gap-8 lg:gap-16">

                                    {/* LEFT: Form Fields */}
                                    <div className="flex flex-col justify-center gap-5 text-white">
                                        <h2 className="text-3xl font-bold text-center"
                                            style={{
                                                background: 'linear-gradient(90deg, #FFCD77, #3c4ef6)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                            }}>
                                            Registration
                                        </h2>

                                        {/* First Name + Last Name */}
                                        <div className="flex gap-3">
                                            <div className="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="firstname"
                                                    value={values.firstname}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    className="peer w-full bg-transparent border border-blue-300/40 rounded-md px-4 py-3 text-white focus:border-blue-500 outline-none"
                                                />
                                                <label className={`absolute left-4 px-2 py-0.5 rounded-full text-sm transition-all duration-200 ${values.firstname ? "-top-3 text-xs text-sky-400 bg-slate-800" : "top-3 text-sm text-blue-300"} peer-focus:-top-3 peer-focus:text-xs peer-focus:text-sky-400 peer-focus:bg-slate-800`}>
                                                    First Name
                                                </label>
                                            </div>
                                            <div className="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="lastname"
                                                    value={values.lastname}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    className="peer w-full bg-transparent border border-blue-300/40 rounded-md px-4 py-3 text-white focus:border-blue-500 outline-none"
                                                />
                                                <label className={`absolute left-4 px-2 py-0.5 rounded-full text-sm transition-all duration-200 ${values.lastname ? "-top-3 text-xs text-sky-400 bg-slate-800" : "top-3 text-sm text-blue-300"} peer-focus:-top-3 peer-focus:text-xs peer-focus:text-sky-400 peer-focus:bg-slate-800`}>
                                                    Last Name
                                                </label>
                                            </div>
                                        </div>

                                        {/* Email */}
                                        <div className="relative w-full">
                                            <input
                                                type="email"
                                                name="email"
                                                value={values.email}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                className="peer w-full bg-transparent border border-blue-300/40 rounded-md px-4 py-3 text-white focus:border-blue-500 outline-none"
                                            />
                                            <label className={`absolute left-4 px-2 py-0.5 rounded-full text-sm transition-all duration-200 ${values.email ? "-top-3 text-xs text-sky-400 bg-slate-800" : "top-3 text-sm text-blue-300"} peer-focus:-top-3 peer-focus:text-xs peer-focus:text-sky-400 peer-focus:bg-slate-800`}>
                                                Email
                                            </label>
                                            {touched.email && errors.email && (
                                                <p className="text-red-400 text-xs mt-1 pl-1">{errors.email}</p>
                                            )}
                                        </div>

                                        {/* Phone */}
                                        <div className="relative w-full">
                                            <input
                                                type="tel"
                                                name="tel"
                                                value={values.tel}
                                                onBlur={handleBlur}
                                                onChange={(e) => {
                                                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                                    handleChange({ target: { name: "tel", value: numericValue } });
                                                }}
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                className="peer w-full bg-transparent border border-blue-300/40 rounded-md px-4 py-3 text-white focus:border-blue-500 outline-none"
                                            />
                                            <label className={`absolute left-4 px-2 py-0.5 rounded-full text-sm transition-all duration-200 ${values.tel ? "-top-3 text-xs text-sky-400 bg-slate-800" : "top-3 text-sm text-blue-300"} peer-focus:-top-3 peer-focus:text-xs peer-focus:text-sky-400 peer-focus:bg-slate-800`}>
                                                Phone Number
                                            </label>
                                        </div>

                                        {/* Buttons */}
                                        <div className="flex gap-3 mt-2">
                                            <button
                                                type="button"
                                                onClick={() => { signUpClose(); resetForm(); }}
                                                className="flex-1 py-3 rounded-lg border border-gray-500 text-gray-300 hover:bg-gray-700 transition-all font-semibold"
                                            >
                                                CANCEL
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleNextPage}
                                                className="flex-1 py-3 rounded-lg bg-sky-600 hover:bg-sky-500 transition-all font-semibold text-white"
                                            >
                                                NEXT
                                            </button>
                                        </div>
                                    </div>

                                    {/* RIGHT: Rocket (hidden on mobile เหมือนไฟล์ 1) */}
                                    <div className="hidden lg:flex items-center justify-center">
                                        <Rocket />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Dialog>

                    {/* ===== OTP Dialog (logic ไฟล์ 1, UI ปรับให้ match ไฟล์ 2) ===== */}
                    <Dialog
                        open={openDialogOtp}
                        TransitionComponent={Transition}
                        keepMounted
                        onClose={handleCloseDialogOtp}
                        maxWidth="xs"
                        fullWidth
                        sx={{ zIndex: theme.zIndex.modal }}
                    >
                        <DialogTitle sx={{ fontSize: "24px", backgroundImage: "linear-gradient(to right, #FFCD77, #A672C1)" }}>
                            {"กรุณากรอก OTP !"}
                        </DialogTitle>
                        <DialogContent sx={{ backgroundImage: "linear-gradient(to right, #FFCD77, #A672C1)" }}>
                            <DialogContentText />
                            <Box display="flex" justifyContent="center" alignItems="center">
                                {otp.map((data, index) => (
                                    <TextField
                                        key={index}
                                        type="tel"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        inputProps={{ maxLength: 1, style: { textAlign: 'center', color: otpVerify === false ? 'red' : '#333' } }}
                                        value={data}
                                        onChange={e => handleChangeOTP(e.target, index)}
                                        onKeyDown={e => handleKeyDown(e, index)}
                                        onKeyUp={e => handleKeyUp(e, index)}
                                        inputRef={el => (inputRefs.current[index] = el)}
                                        autoComplete="off"
                                        sx={{
                                            marginRight: index < 5 ? 1 : 0, width: '40px',
                                            background: '#D7AE97', borderRadius: '15px', border: 0,
                                            padding: '10px',
                                            boxShadow: '2px 2px 2px rgba(0,0,0,0.2), 2px 0px 3px rgba(0,0,0,0.5)',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '30px', background: 'inherit',
                                                '& fieldset': { border: 'none' },
                                                '&:hover fieldset': { border: 'none' },
                                                '&.Mui-focused fieldset': { border: 'none' },
                                            },
                                            '& .MuiInputBase-input': {
                                                padding: 0, textAlign: 'center',
                                                fontSize: '1rem', fontWeight: 600,
                                                letterSpacing: '1px', color: '#333',
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                            <Box display="flex" justifyContent="center" mt={1}>
                                {otpVerify === false && (
                                    <Typography variant="body2" color="error">
                                        OTP verification failed. Please check your OTP.
                                    </Typography>
                                )}
                            </Box>
                        </DialogContent>
                    </Dialog>

                    {/* ===== Forget Password Dialog (จากไฟล์ 1) ===== */}
                    <Dialog open={open} onClose={forgetPasswordClose}>
                        <DialogTitle fontSize='24px'>Forget your ID or password?</DialogTitle>
                        <DialogContent>
                            <Box sx={{ width: { xs: '70vw', sm: '50vw', md: '30vw' } }}>
                                <Typography color='white' fontSize='16px'>
                                    Your ID is: your <span style={{ fontSize: '20px', color: '#3445DD', fontWeight: 'bold', paddingLeft: '5px' }}>Email address.</span>
                                </Typography>
                                <Typography color='white' fontSize='16px'>
                                    Your Password is: your <span style={{ fontSize: '20px', color: '#3445DD', fontWeight: 'bold', paddingLeft: '5px' }}>Phone number.</span>
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={forgetPasswordClose}>Close</Button>
                        </DialogActions>
                    </Dialog>

                    {/* ===== Error Popup (UI ไฟล์ 2) ===== */}
                    {openErrorPopup && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center">
                            <div
                                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                                onClick={() => setOpenErrorPopup(false)}
                            />
                            <div className="relative z-10 w-[90%] max-w-md mx-auto rounded-2xl bg-slate-800 text-white p-6 shadow-xl">
                                {popupType === "loginFailed" && (
                                    <>
                                        <h3 className="text-xl font-semibold mb-2 text-red-400">Login Failed</h3>
                                        <p className="text-sm text-gray-300 mb-5">{isLoginStatusText || "Please check your credentials and try again."}</p>
                                    </>
                                )}
                                <div className="flex justify-end">
                                    <button
                                        onClick={() => setOpenErrorPopup(false)}
                                        className="px-5 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all font-medium"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Backdrop */}
                    <Backdrop
                        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 9999 }}
                        open={openBackdrop}
                        onClick={handleCloseBackdrop}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>

                </form>
            )}
        </Formik>
    );
};

export default Form;